import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Card, Form, Button, Alert, Row, Col, Badge, Spinner, Table } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { formatCurrency } from '../../utils/formatters';
import moment from 'moment';

const SharedReportView = () => {
    const { token } = useParams();
    const [accessCode, setAccessCode] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState(null);

    // Auto-verify if code is in URL (used by PDF generator)
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('accessCode');
        if (code && code.length === 6) {
            setAccessCode(code);
            autoVerify(code);
        }
    }, []);

    const autoVerify = async (code) => {
        setLoading(true);
        try {
            const res = await axios.post(`/api/public/reports/view/${token}`, { accessCode: code });
            setReportData(res.data);
            setIsVerified(true);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to verify');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(`/api/public/reports/view/${token}`, { accessCode });
            setReportData(res.data);
            setIsVerified(true);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to verify access code');
        } finally {
            setLoading(false);
        }
    };

    if (!isVerified) {
        return (
            <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
                <Card style={{ maxWidth: '400px', width: '100%' }} className="shadow-sm border-0">
                    <Card.Body className="p-4 text-center">
                        <div className="mb-4">
                            <i className="fas fa-lock fa-3x text-primary mb-3"></i>
                            <h4>Secure Financial Report</h4>
                            <p className="text-muted small">Please enter the 6-digit access code sent to your email to view this report.</p>
                        </div>

                        {error && <Alert variant="danger" className="small py-2">{error}</Alert>}

                        <Form onSubmit={handleVerify}>
                            <Form.Group>
                                <Form.Control 
                                    type="text" 
                                    placeholder="Enter 6-digit code" 
                                    className="text-center font-weight-bold"
                                    style={{ letterSpacing: '0.5rem', fontSize: '1.2rem' }}
                                    value={accessCode}
                                    onChange={e => setAccessCode(e.target.value)}
                                    maxLength={6}
                                />
                            </Form.Group>
                            <Button 
                                variant="primary" 
                                block 
                                className="mt-4" 
                                type="submit"
                                disabled={loading || accessCode.length < 6}
                            >
                                {loading ? <Spinner animation="border" size="sm" /> : 'View Report'}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    const renderBalanceSheetSection = (groupedData) => {
        if (!groupedData) return null;
        const { assets, liabilities, equity } = groupedData;
        
        const renderSubCategoryRows = (catName, catData, detailIndentClass = "pl-5", headerIndentClass = "pl-4") => {
            if (!catData || !catData.details || Object.keys(catData.details).length === 0) return null;
            return (
                <>
                    <tr className="font-weight-bold text-muted small"><td className={`${headerIndentClass} text-uppercase`}>{catName}</td><td></td></tr>
                    {Object.entries(catData.details).map(([name, amount]) => (
                        <tr key={name} className="extra-small">
                            <td className={detailIndentClass}>{name}</td>
                            <td className="text-right">{formatCurrency(amount)}</td>
                        </tr>
                    ))}
                    <tr className="font-weight-bold extra-small text-muted"><td className={headerIndentClass}>Total for {catName}</td><td className="text-right border-top">{formatCurrency(catData.total)}</td></tr>
                </>
            );
        };

        return (
            <Table responsive borderless className="report-table mb-0">
                <tbody>
                    {/* ASSETS */}
                    <tr className="bg-light font-weight-bold"><td colSpan="2" className="text-uppercase text-primary pl-1">ASSETS</td></tr>
                    
                    {/* Current Assets */}
                    <tr className="font-weight-bold small"><td className="pl-3">Current Assets</td><td></td></tr>
                    {renderSubCategoryRows("Bank Accounts", assets.currentAssets.bank, "pl-5", "pl-4")}
                    {renderSubCategoryRows("Accounts Receivable", assets.currentAssets.accountsReceivable, "pl-5", "pl-4")}
                    {renderSubCategoryRows("Other Current Assets", assets.currentAssets.otherCurrentAssets, "pl-5", "pl-4")}
                    <tr className="font-weight-bold small"><td className="pl-3">Total for Current Assets</td><td className="text-right border-top border-dark">{formatCurrency(assets.currentAssets.total)}</td></tr>

                    {/* Fixed Assets */}
                    {assets.fixedAssets && Object.keys(assets.fixedAssets.details).length > 0 && (
                        <>
                            <tr className="font-weight-bold small mt-2"><td className="pl-3">Fixed Assets</td><td></td></tr>
                            {Object.entries(assets.fixedAssets.details).map(([name, amount]) => (
                                <tr key={name} className="extra-small">
                                    <td className="pl-4">{name}</td>
                                    <td className="text-right">{formatCurrency(amount)}</td>
                                </tr>
                            ))}
                            <tr className="font-weight-bold small"><td className="pl-3">Total for Fixed Assets</td><td className="text-right border-top">{formatCurrency(assets.fixedAssets.total)}</td></tr>
                        </>
                    )}

                    {/* Other Assets */}
                    {assets.otherAssets && Object.keys(assets.otherAssets.details).length > 0 && (
                        <>
                            <tr className="font-weight-bold small mt-2"><td className="pl-3">Other Assets</td><td></td></tr>
                            {Object.entries(assets.otherAssets.details).map(([name, amount]) => (
                                <tr key={name} className="extra-small">
                                    <td className="pl-4">{name}</td>
                                    <td className="text-right">{formatCurrency(amount)}</td>
                                </tr>
                            ))}
                            <tr className="font-weight-bold small"><td className="pl-3">Total for Other Assets</td><td className="text-right border-top">{formatCurrency(assets.otherAssets.total)}</td></tr>
                        </>
                    )}

                    <tr className="h5 font-weight-bold border-top border-bottom border-dark bg-light text-primary">
                        <td className="pl-1">TOTAL ASSETS</td>
                        <td className="text-right">{formatCurrency(assets.total)}</td>
                    </tr>

                    {/* LIABILITIES AND EQUITY */}
                    <tr className="bg-light font-weight-bold"><td colSpan="2" className="text-uppercase text-primary mt-4 pl-1">LIABILITIES AND EQUITY</td></tr>
                    
                    {/* Liabilities */}
                    <tr className="font-weight-bold small"><td className="pl-3">Liabilities</td><td></td></tr>
                    
                    {/* Current Liabilities */}
                    <tr className="font-weight-bold small"><td className="pl-4 text-muted">Current Liabilities</td><td></td></tr>
                    {renderSubCategoryRows("Accounts Payable", liabilities.currentLiabilities.accountsPayable, "pl-6", "pl-5")}
                    {renderSubCategoryRows("Credit Card", liabilities.currentLiabilities.creditCard, "pl-6", "pl-5")}
                    {renderSubCategoryRows("Other Current Liabilities", liabilities.currentLiabilities.otherCurrentLiabilities, "pl-6", "pl-5")}
                    <tr className="font-weight-bold small"><td className="pl-4">Total for Current Liabilities</td><td className="text-right border-top">{formatCurrency(liabilities.currentLiabilities.total)}</td></tr>

                    {/* Long Term Liabilities */}
                    {liabilities.longTermLiabilities && Object.keys(liabilities.longTermLiabilities.details).length > 0 && (
                        <>
                            <tr className="font-weight-bold small mt-2"><td className="pl-4">Long Term Liabilities</td><td></td></tr>
                            {Object.entries(liabilities.longTermLiabilities.details).map(([name, amount]) => (
                                <tr key={name} className="extra-small">
                                    <td className="pl-5">{name}</td>
                                    <td className="text-right">{formatCurrency(amount)}</td>
                                </tr>
                            ))}
                            <tr className="font-weight-bold small"><td className="pl-4">Total for Long Term Liabilities</td><td className="text-right border-top">{formatCurrency(liabilities.longTermLiabilities.total)}</td></tr>
                        </>
                    )}

                    <tr className="font-weight-bold small"><td className="pl-3">Total for Liabilities</td><td className="text-right border-top border-dark">{formatCurrency(liabilities.total)}</td></tr>

                    {/* Equity */}
                    <tr className="font-weight-bold small mt-2"><td className="pl-3">Equity</td><td></td></tr>
                    {Object.entries(equity.details).map(([name, amount]) => (
                        <tr key={name} className="extra-small">
                            <td className="pl-4">{name}</td>
                            <td className="text-right">{formatCurrency(amount)}</td>
                        </tr>
                    ))}
                    <tr className="font-weight-bold small"><td className="pl-3">Total for Equity</td><td className="text-right border-top">{formatCurrency(equity.total)}</td></tr>

                    {/* Total Liabilities and Equity */}
                    <tr className="h5 font-weight-bold border-top border-bottom border-dark bg-dark text-white">
                        <td className="pl-1">TOTAL LIABILITIES AND EQUITY</td>
                        <td className="text-right">{formatCurrency(liabilities.total + equity.total)}</td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    return (
        <Container className="py-5">
            <div className="mb-5 text-center">
                <Badge variant="primary" className="mb-2">Confidential Financial Brief</Badge>
                <h1 className="h2">{reportData.projectName}</h1>
                <p className="text-muted">
                    {new Date(reportData.year, reportData.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </p>
                <hr style={{ width: '50px', borderTop: '3px solid #007bff', margin: '20px auto' }} />
            </div>

            <Row className="mb-4">
                <Col md={4}>
                    <Card className="text-center border-0 shadow-sm mb-3">
                        <Card.Body>
                            <small className="text-muted text-uppercase font-weight-bold">Net Income</small>
                            <h3 className={reportData.pnl.netIncome >= 0 ? 'text-success' : 'text-danger'}>
                                {formatCurrency(reportData.pnl.netIncome)}
                            </h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center border-0 shadow-sm mb-3">
                        <Card.Body>
                            <small className="text-muted text-uppercase font-weight-bold">Total Revenue</small>
                            <h3 className="text-primary">{formatCurrency(reportData.pnl.totalIncome)}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="text-center border-0 shadow-sm mb-3">
                        <Card.Body>
                            <small className="text-muted text-uppercase font-weight-bold">Gross Margin</small>
                            <h3>{formatCurrency(reportData.pnl.grossMargin)}</h3>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm mb-5">
                <Card.Body className="p-4 p-md-5">
                    <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                        <h4 className="mb-0 text-primary">
                            <i className="fas fa-robot mr-2"></i>AI Executive Analysis
                        </h4>
                        {reportData.dateGenerated && (
                            <small className="text-muted">
                                <i className="far fa-clock mr-1"></i>
                                {moment(reportData.dateGenerated).format('MMM D, YYYY h:mm A')}
                            </small>
                        )}
                    </div>
                    <div className="ai-report-content mb-5" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
                        <ReactMarkdown components={{
                            h3: ({node, ...props}) => <h5 className="text-dark mt-4 mb-2 font-weight-bold" {...props} />,
                            h4: ({node, ...props}) => <h6 className="font-weight-bold mt-3" {...props} />,
                            ul: ({node, ordered, ...props}) => <ul className="pl-3" {...props} />,
                            li: ({node, ordered, ...props}) => <li className="mb-2" {...props} />,
                            strong: ({node, ...props}) => <strong className="text-primary" {...props} />
                        }}>
                            {reportData.aiSummary}
                        </ReactMarkdown>
                    </div>

                    <h4 className="mb-4 text-dark border-bottom pb-2 mt-5">
                        <i className="fas fa-file-invoice-dollar mr-2"></i>Detailed Financial Statements
                    </h4>

                    {/* P&L Table */}
                    <div className="mb-5">
                        <h5 className="bg-light p-2 rounded small font-weight-bold mb-3">PROFIT AND LOSS</h5>
                        <Table responsive hover size="sm" className="mb-0">
                            <tbody>
                                <tr className="font-weight-bold"><td>Total Income</td><td className="text-right">{formatCurrency(reportData.pnl.totalIncome)}</td></tr>
                                {Object.entries(reportData.pnl.details.income).map(([name, val]) => (
                                    <tr key={name} className="small text-muted"><td className="pl-4">{name}</td><td className="text-right">{formatCurrency(val)}</td></tr>
                                ))}
                                <tr className="font-weight-bold"><td>Total COGS</td><td className="text-right">({formatCurrency(reportData.pnl.totalCOGS)})</td></tr>
                                {Object.entries(reportData.pnl.details.cogs).map(([name, val]) => (
                                    <tr key={name} className="small text-muted"><td className="pl-4">{name}</td><td className="text-right">{formatCurrency(val)}</td></tr>
                                ))}
                                <tr className="h6 font-weight-bold border-top"><td>Gross Margin</td><td className="text-right">{formatCurrency(reportData.pnl.grossMargin)}</td></tr>
                                <tr className="font-weight-bold mt-2"><td>Total Operating Expenses</td><td className="text-right">({formatCurrency(reportData.pnl.totalExpenses)})</td></tr>
                                {Object.entries(reportData.pnl.details.expenses).map(([name, val]) => (
                                    <tr key={name} className="small text-muted"><td className="pl-4">{name}</td><td className="text-right">{formatCurrency(val)}</td></tr>
                                ))}
                                <tr className="h5 font-weight-bold bg-dark text-white"><td>Net Income</td><td className="text-right">{formatCurrency(reportData.pnl.netIncome)}</td></tr>
                            </tbody>
                        </Table>
                    </div>

                    {/* Balance Sheet Table */}
                    {reportData.groupedData ? (
                        renderBalanceSheetSection(reportData.groupedData)
                    ) : (
                        <Row>
                            <Col lg={6}>
                                <h5 className="bg-light p-2 rounded small font-weight-bold mb-3">ASSETS</h5>
                                <Table responsive hover size="sm">
                                    <tbody>
                                        <tr className="font-weight-bold"><td>TOTAL ASSETS</td><td className="text-right">{formatCurrency(reportData.balanceSheet.assets.total)}</td></tr>
                                        {Object.entries(reportData.balanceSheet.assets.details).map(([name, val]) => (
                                            <tr key={name} className="small text-muted"><td className="pl-4">{name}</td><td className="text-right">{formatCurrency(val)}</td></tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Col>
                            <Col lg={6}>
                                <h5 className="bg-light p-2 rounded small font-weight-bold mb-3">LIABILITIES & EQUITY</h5>
                                <Table responsive hover size="sm">
                                    <tbody>
                                        <tr className="font-weight-bold"><td>TOTAL LIABILITIES</td><td className="text-right">{formatCurrency(reportData.balanceSheet.liabilities.total)}</td></tr>
                                        {Object.entries(reportData.balanceSheet.liabilities.details).map(([name, val]) => (
                                            <tr key={name} className="small text-muted"><td className="pl-4">{name}</td><td className="text-right">{formatCurrency(val)}</td></tr>
                                        ))}
                                        <tr className="font-weight-bold mt-2"><td>TOTAL EQUITY</td><td className="text-right">{formatCurrency(reportData.balanceSheet.equity.total)}</td></tr>
                                        {Object.entries(reportData.balanceSheet.equity.details).map(([name, val]) => (
                                            <tr key={name} className="small text-muted"><td className="pl-4">{name}</td><td className="text-right">{formatCurrency(val)}</td></tr>
                                        ))}
                                        <tr className="h6 font-weight-bold border-top text-primary">
                                            <td>TOTAL L&E</td>
                                            <td className="text-right">{formatCurrency(reportData.balanceSheet.liabilities.total + reportData.balanceSheet.equity.total)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            <div className="text-center text-muted mt-5 mb-5 small">
                <p>&copy; {new Date().getFullYear()} {reportData.projectName}. Generated via Secure Bookeeping AI Reporter.</p>
                <div className="mt-2">
                    <Button 
                        variant="primary" 
                        size="sm" 
                        className="mr-2"
                        onClick={() => window.open(`http://localhost:5001/api/public/reports/download/${token}?accessCode=${accessCode}`, '_blank')}
                    >
                        <i className="fas fa-file-pdf mr-2"></i>Download Official PDF
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => window.print()}>
                        <i className="fas fa-print mr-2"></i>Print Page
                    </Button>
                </div>
            </div>
        </Container>
    );
};

export default SharedReportView;
